import '@styles/variables.css';
import '@styles/tailwind.css';
import '@styles/globals.css';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';
import 'swiper/css/scrollbar';

import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import Head from 'next/head';
import { DefaultSeo } from 'next-seo';
import { APP_DESCRIPTION, APP_META_KEYWORDS, APP_TITLE, APP_URL, PLACEHOLDER_IMAGES } from '@/constants';
import AppContainer from '@/components/AppContainer';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AxiosInterceptorComponent } from '@/components/AxiosInterceptorComponent';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { RouterTransition } from '@/components/RouterTransition';
import { SessionProvider } from 'next-auth/react';
import { useRouter } from 'next/router';
//import { rtlCache } from '../rtl-cache';
import { Figtree } from 'next/font/google';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
const figtree = Figtree({ subsets: ['latin'], weight: ['400', '500', '600', '700'], display: 'swap', fallback: ['"Segoe UI"', 'sans-serif'] });
export function ApexApp({ Component, pageProps: { session, ...pageProps } }: AppProps) {
  const [queryClient] = useState(new QueryClient());
  const router = useRouter();
  const [showDevtools, setShowDevtools] = useState(false);
  const [showChatIcon, setShowChatIcon] = useState(true);

  const ReactQueryDevtoolsProduction = lazy(() =>
    import('@tanstack/react-query-devtools/build/lib/index.prod.js').then((d) => ({
      default: d.ReactQueryDevtools,
    })),
  );

  useEffect(() => {
    const setScrollPosition = (position: number) => {
      window.scrollTo(0, position);
      document.body.scrollTop = position;
      document.documentElement.scrollTop = position; // Set scroll for most devices/browsers
    };

    const getScrollPosition = () => {
      return window.scrollY || document.body.scrollTop || document.documentElement.scrollTop || 0;
    };
    const saveScrollPosition = (url: string) => {
      const scrollPosition = getScrollPosition().toString();
      sessionStorage.setItem(`scrollPosition_${url}`, scrollPosition);
    };

    const restoreScrollPosition = (url: string) => {
      const savedPosition = sessionStorage.getItem(`scrollPosition_${url}`);
      const isProductPage = url.startsWith('/product/');

      if (isProductPage) {
        setScrollPosition(0);
      } else {
        if (savedPosition !== null) {
          setScrollPosition(parseInt(savedPosition, 10));
        } else {
          setScrollPosition(0);
        }
      }
    };

    const handleRouteChangeStart = (url: string) => {
      saveScrollPosition(router.asPath);
    };

    const handleRouteChangeComplete = (url: string) => {
      restoreScrollPosition(url);
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router]);

  useEffect(() => {
    document.body.classList.remove('floating-cart-visible');
  }, [router.asPath]);
  useEffect(() => {
    // @ts-ignore
    window.toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  useEffect(() => {
    if (router.isReady && (router.pathname === '/feedback' || router.query.isFeedbackUrl)) {
      console.log(router.isReady && (router.pathname === '/feedback' || router.query.isFeedbackUrl));

      setShowChatIcon(false);
    }
  }, [router.isReady, router.pathname, router.query]);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {!(router.pathname === '/feedback' || router.query.isFeedbackUrl) && <script src="/liveChat.js" async defer></script>}
      </Head>
      <SessionProvider session={session}>
        <DefaultSeo
          key="DefaultSeo"
          title={APP_TITLE}
          description={APP_DESCRIPTION}
          openGraph={{
            type: 'website',
            locale: 'en_IE',
            url: APP_URL,
            site_name: APP_TITLE,
            images: [
              {
                url: PLACEHOLDER_IMAGES.DEFAULT_OG_IMAGE,
                alt: 'Transom Digital Logo',
                type: 'image/jpeg',
              },
            ],
          }}
          additionalMetaTags={[
            {
              property: 'keywords',
              content: APP_META_KEYWORDS || '',
            },
          ]}
        />
        <QueryClientProvider client={queryClient}>
          <Hydrate state={pageProps.dehydratedState}>
            <MantineProvider
              withGlobalStyles
              withNormalizeCSS
              //emotionCache={rtlCache}
              theme={{ fontFamily: figtree.style.fontFamily, lineHeight: '1.5' }}
            >
              <RouterTransition />
              <Notifications position="bottom-left" />
              <AxiosInterceptorComponent />
              <AppContainer Component={Component} pageProps={pageProps}></AppContainer>
              <ReactQueryDevtools initialIsOpen={false} />
              {showDevtools && (
                <Suspense fallback={null}>
                  <ReactQueryDevtoolsProduction />
                </Suspense>
              )}
            </MantineProvider>
          </Hydrate>
        </QueryClientProvider>
      </SessionProvider>
    </>
  );
}

export default ApexApp;
